import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { MODE, ROBOT_STATUS } from '@/define/robotStatus';

// MavLink 프로토콜을 사용하는 드론의 제조사별, 기체별 모드 매핑
const MAVLINK_MODE_MAP = {
  ArduPilot: {
    Copter: MODE.ARDUPILOT.COPTER,
    Plane: MODE.ARDUPILOT.PLANE,
    Rover: MODE.ARDUPILOT.ROVER,
  },
  PX4: {
    Copter: MODE.PX4.COPTER,
    Rover: MODE.PX4.ROVER,
  },
};

// heartbeat 모드 ID를 기반으로 MavLink 드론의 실제 모드명 반환
const getMavLinkMode = (maker, vehicle, heartbeatMode) => {
  const makerModes = MAVLINK_MODE_MAP[maker];
  const vehicleModes = makerModes[vehicle];

  return Object.values(vehicleModes).find(({ id }) => id === heartbeatMode)?.name || null;
};

// osd 모드 코드를 기반으로 DJI 드론의 실제 모드명 반환
const getDJIMode = (modeCode) => {
  return Object.values(MODE.DJI).find(({ id }) => id === modeCode)?.name || null;
};

// statusCode를 기반으로 시스템 상태명 반환
const getSystemStatusMode = (statusCode) => {
  return Object.values(ROBOT_STATUS).find(({ code }) => code === statusCode)?.name || null;
};

// 로봇의 에이전트 타입과 statusCode, heartbeat mode에 따른 모드 반환
const getModeByAgentType = (robotModel, statusCode, heartbeatMode, droneModeCode, dockModeCode) => {
  const { agentType, maker, vehicle } = robotModel;

  switch (agentType) {
    case 'MavLink':
      // heartbeat mode 있으면 반환
      if (heartbeatMode !== null) {
        return getMavLinkMode(maker, vehicle, heartbeatMode);
      }
      break;

    case 'DJI':
      // 드론 모드 우선, 도크 모드는 후순위로 표시
      // 느슨한 비교(!=)로 null과 undefined를 모두 체크
      if (droneModeCode != null) {
        return getDJIMode(droneModeCode);
      }
      if (dockModeCode != null) {
        return getDJIMode(dockModeCode);
      }
      break;

    case 'IoT':
      // 온라인 상태면 'ON' 반환
      if (statusCode === ROBOT_STATUS.ONLINE.code) {
        return 'ON';
      }
      break;

    default:
      return null;
  }

  // MavLink/IoT에서 모드가 정의되지 않은 경우 시스템 상태 반환
  return getSystemStatusMode(statusCode);
};

// 로봇의 현재 모드를 반환하는 커스텀 훅
const useModeByStatus = (robot) => {
  const statusCode = useSelector((state) => state.robotStatus[robot.id]?.code);
  const heartbeatMode = useSelector((state) => state.telemetry[robot.id]?.heartbeat?.mode);
  const droneModeCode = useSelector((state) => state.telemetry[robot.id]?.droneOsd?.mode_code);
  const dockModeCode = useSelector((state) => state.telemetry[robot.id]?.dockOsdStatus?.mode_code);

  const mode = useMemo(() => {
    return getModeByAgentType(robot.model, statusCode, heartbeatMode, droneModeCode, dockModeCode);
  }, [robot.model, statusCode, heartbeatMode, droneModeCode, dockModeCode]);

  return mode;
};

export default useModeByStatus;
